import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Loader2, Search, Sparkles, ArrowRight, Bookmark, BookmarkCheck, Trash2, ShoppingCart, BarChart3, ChevronDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const MODE_LABEL = { nonfiction: "Non-fiction", fiction: "Fiction", children: "Children's", journal: "Journal", workbook: "Workbook", sudoku: "Puzzles", maze: "Puzzles", coloring: "Coloring", wordsearch: "Puzzles" };

const sameIdea = (a, b) => (a.title || "").trim() === (b.title || "").trim() && (a.mode || "nonfiction") === (b.mode || "nonfiction");

export const MarketResearch = ({ onPick }) => {
  const [niche, setNiche] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState([]);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [live, setLive] = useState({}); // index -> {loading, items, message, open}

  const loadSaved = useCallback(async () => {
    try {
      const res = await api.listSavedResearch();
      setSaved(res.items || []);
    } catch (e) { /* silent — panel just stays empty */ }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const run = async () => {
    setLoading(true);
    setLive({});
    try {
      setData(await api.marketResearch(niche));
    } catch (e) {
      toast.error(apiError(e, "Market research failed."));
    } finally {
      setLoading(false);
    }
  };

  const isSaved = (s) => saved.some((x) => sameIdea(x, s));

  const toggleSave = async (s) => {
    const key = `${s.title}|${s.mode}`;
    setSavingKey(key);
    try {
      const existing = saved.find((x) => sameIdea(x, s));
      if (existing) {
        await api.deleteSavedResearch(existing.id);
        setSaved((prev) => prev.filter((x) => x.id !== existing.id));
        toast.success("Removed from your shortlist");
      } else {
        const doc = await api.saveResearch({ title: s.title || "", topic: s.topic || s.title || "", category: s.category || "", mode: s.mode || "nonfiction" });
        setSaved((prev) => [doc, ...prev.filter((x) => x.id !== doc.id)]);
        toast.success("Saved to your shortlist");
      }
    } catch (e) {
      toast.error(apiError(e, "Couldn't update your shortlist."));
    } finally {
      setSavingKey(null);
    }
  };

  const removeSaved = async (id) => {
    try {
      await api.deleteSavedResearch(id);
      setSaved((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiError(e, "Couldn't remove that idea."));
    }
  };

  const loadLive = async (i, s) => {
    setLive((prev) => {
      const cur = prev[i];
      if (cur && cur.items !== undefined) return { ...prev, [i]: { ...cur, open: !cur.open } };
      return { ...prev, [i]: { loading: true, open: true } };
    });
    const already = live[i];
    if (already && already.items !== undefined) return;
    try {
      const res = await api.marketAmazon(s.title || s.category || s.topic || "", 5);
      setLive((prev) => ({ ...prev, [i]: { loading: false, open: true, items: res.items || [], message: res.message, configured: res.configured } }));
    } catch (e) {
      setLive((prev) => ({ ...prev, [i]: { loading: false, open: true, items: [], message: apiError(e, "Amazon lookup failed.") } }));
    }
  };

  const chip = "font-ui text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full";

  return (
    <motion.div
      data-testid="market-research"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="bg-navy-900/70 border border-gold-500/12 rounded-2xl p-5 sm:p-6 shadow-xl max-w-3xl mx-auto mt-8 mb-4"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 font-ui text-[11px] uppercase tracking-[0.25em] text-slate-400/90 mb-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Market research
          </div>
          <h2 className="font-display text-lg sm:text-xl font-bold text-slate-100">Not sure what to write? See what sells.</h2>
          <p className="font-read text-sm text-slate-400 mt-1 max-w-xl">
            The top profitable categories plus ready-to-write ideas — tap one to load it into the creator.
          </p>
        </div>
        <button
          data-testid="saved-research-toggle"
          onClick={() => setSavedOpen((v) => !v)}
          className="shrink-0 flex items-center gap-1.5 rounded-full border border-gold-500/12 bg-navy-950/50 hover:border-gold-400/50 hover:text-gold-200 text-slate-300 font-ui text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          <Bookmark className="w-3.5 h-3.5" />
          Saved
          {saved.length > 0 && <span data-testid="saved-count-badge" className="min-w-[18px] text-center rounded-full bg-gold-400 text-navy-900 text-[10px] font-bold px-1.5 py-0.5">{saved.length}</span>}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${savedOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      {savedOpen && (
        <div data-testid="saved-research-panel" className="mb-6 rounded-xl border border-gold-500/12 bg-navy-950/50 p-4">
          <h3 className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-3">Your shortlist</h3>
          {saved.length === 0 ? (
            <p data-testid="saved-empty" className="font-read text-sm text-slate-500">No saved ideas yet. Tap the bookmark on any idea to keep it here for later.</p>
          ) : (
            <div className="space-y-2">
              {saved.map((s) => (
                <div key={s.id} data-testid={`saved-item-${s.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-gold-500/12 bg-navy-900/50 p-3">
                  <div className="min-w-0">
                    <div className="font-ui text-sm font-semibold text-slate-100 truncate">{s.title}</div>
                    <div className="font-ui text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-navy-800 text-slate-300">{MODE_LABEL[s.mode] || "Non-fiction"}</span>
                      {s.category && <span className="truncate">{s.category}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    <button
                      data-testid={`saved-write-${s.id}`}
                      onClick={() => onPick?.(s)}
                      className="flex items-center gap-1 rounded-lg klyr-btn-metal font-ui text-xs font-semibold px-3 py-1.5 transition-all"
                    >
                      Write <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`saved-remove-${s.id}`}
                      onClick={() => removeSaved(s.id)}
                      className="p-1.5 rounded-lg text-slate-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="market-niche-input"
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
            placeholder="Optional: a niche you like (e.g. fitness, finance, kids)"
            className="w-full bg-navy-950/60 border border-gold-500/12 rounded-xl pl-9 pr-3 py-3 font-ui text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-gold-400 transition-colors"
          />
        </div>
        <button
          data-testid="market-research-run"
          onClick={run}
          disabled={loading}
          className="flex items-center justify-center gap-2 klyr-btn-metal font-ui font-semibold text-sm rounded-xl px-6 py-3 transition-all disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Analyzing…" : "Find profitable ideas"}
        </button>
      </div>

      {data && (
        <div className="mt-7 space-y-6" data-testid="market-results">
          <div>
            <h3 className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-3">Top 5 profitable categories</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.categories.map((c, i) => (
                <div key={i} data-testid={`market-category-${i}`} className="rounded-xl border border-gold-500/12 bg-navy-950/50 p-4">
                  <div className="flex items-start gap-2">
                    <span className="font-display text-2xl font-bold text-slate-400/80 leading-none">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="font-ui text-sm font-semibold text-slate-100">{c.name}</div>
                      <p className="font-read text-xs text-slate-400 mt-1">{c.why}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className={`${chip} bg-emerald-500/15 text-emerald-300`}>Demand: {c.demand}</span>
                        <span className={`${chip} bg-navy-800 text-slate-300`}>Competition: {c.competition}</span>
                        {c.price_range && <span className={`${chip} bg-gold-400/15 text-slate-200`}>{c.price_range}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90">Books likely to sell</h3>
              <span className={`${chip} ${data.live_enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-navy-800 text-slate-400"}`}>
                {data.live_enabled ? "Live Amazon connected" : "AI analysis"}
              </span>
            </div>
            <div className="space-y-2">
              {data.suggestions.map((s, i) => {
                const lv = live[i] || {};
                return (
                  <div key={i} data-testid={`market-suggestion-${i}`} className="rounded-xl border border-gold-500/12 bg-navy-950/50 overflow-hidden">
                    <div className="flex items-center justify-between gap-3 p-4">
                      <button
                        data-testid={`market-suggestion-pick-${i}`}
                        onClick={() => onPick?.(s)}
                        className="group min-w-0 text-left flex-1"
                      >
                        <div className="font-ui text-sm font-semibold text-slate-100 truncate group-hover:text-gold-200 transition-colors">{s.title}</div>
                        <div className="font-ui text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                          <span className="px-1.5 py-0.5 rounded bg-navy-800 text-slate-300">{MODE_LABEL[s.mode] || "Non-fiction"}</span>
                          <span className="truncate">{s.category}</span>
                        </div>
                      </button>
                      <div className="shrink-0 flex items-center gap-1">
                        <button
                          data-testid={`market-live-${i}`}
                          onClick={() => loadLive(i, s)}
                          className={`flex items-center gap-1 rounded-lg border font-ui text-[11px] font-medium px-2.5 py-1.5 transition-colors ${lv.open ? "border-gold-400/50 text-slate-200 bg-gold-400/10" : "border-gold-500/12 text-slate-300 hover:border-gold-400/40 hover:text-gold-200"}`}
                          title="See live competing books on Amazon"
                        >
                          {lv.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                          Live Amazon
                        </button>
                        <button
                          data-testid={`market-save-${i}`}
                          onClick={() => toggleSave(s)}
                          disabled={savingKey === `${s.title}|${s.mode}`}
                          className={`p-1.5 rounded-lg transition-colors ${isSaved(s) ? "text-slate-300 bg-gold-400/10" : "text-slate-400 hover:text-gold-200 hover:bg-navy-800"}`}
                          title={isSaved(s) ? "Remove from shortlist" : "Save to shortlist"}
                        >
                          {isSaved(s) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                        </button>
                        <button
                          data-testid={`market-suggestion-write-${i}`}
                          onClick={() => onPick?.(s)}
                          className="flex items-center gap-1 font-ui text-xs font-medium text-slate-300 hover:translate-x-0.5 transition-transform px-1"
                        >
                          Write <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {lv.open && (
                      <div data-testid={`market-live-results-${i}`} className="border-t border-gold-500/12 bg-navy-950/60 px-4 py-3">
                        {lv.loading ? (
                          <div className="flex items-center gap-2 text-slate-400 font-ui text-xs"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking Amazon…</div>
                        ) : (lv.items && lv.items.length > 0) ? (
                          <div className="space-y-2">
                            {lv.items.map((it, j) => (
                              <a
                                key={j}
                                href={it.url || "#"}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`market-live-item-${i}-${j}`}
                                className="flex items-center gap-3 rounded-lg border border-gold-500/12 hover:border-gold-400/40 bg-navy-900/50 p-2.5 transition-colors"
                              >
                                {it.image && <img src={it.image} alt="" className="w-9 h-12 object-cover rounded shrink-0" />}
                                <div className="min-w-0 flex-1">
                                  <div className="font-ui text-xs font-semibold text-slate-100 truncate">{it.title || "Untitled"}</div>
                                  {it.author && <div className="font-ui text-[11px] text-slate-500 truncate">{it.author}</div>}
                                </div>
                                <div className="shrink-0 flex items-center gap-2">
                                  {it.price && <span className="font-ui text-[11px] font-semibold text-emerald-300">{it.price}</span>}
                                  {it.bsr != null && (
                                    <span className="flex items-center gap-1 font-ui text-[10px] text-slate-400"><BarChart3 className="w-3 h-3" /> #{Number(it.bsr).toLocaleString()}</span>
                                  )}
                                </div>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p data-testid={`market-live-empty-${i}`} className="font-ui text-xs text-slate-500">{lv.message || "No live results right now."}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {data.note && <p className="font-ui text-[11px] text-slate-500">{data.note}</p>}
        </div>
      )}
    </motion.div>
  );
};
